import React, {ReactNode} from 'react';

import useResolvedChipItemIdMap from 'insights/analytics-visualizations/chip-items/useResolvedChipItemIdMap';
import {ExperimentalShapashTableOptions} from 'machine-learning/handleClickShapashTableRow';
import {CellRenderOptions} from 'ui-library/simple-table/SimpleTable';
import {JSONValue} from 'util/Types';

import {RunQueryOutputLegacyData} from './AnalyticsWarehouseTypes';
import cellToString from './cellToString';
import RunQueryOutputTable from './RunQueryOutputTable';
import SortableRunQueryOutputTableHeaderCell, {
  ColumnSortState,
} from './SortableRunQueryOutputTableHeaderCell';
import sortedRunQueryOutput from './sortedRunQueryOutput';
import {RunQueryOutputTableSortState} from './useRunQueryOutputTableSortState';

export interface SortableRunQueryOutputTableProps {
  renderCellContent(cell: JSONValue, options: CellRenderOptions): ReactNode;
  runQueryOutput: RunQueryOutputLegacyData;
  rawOutputMode: boolean;
  sortState: RunQueryOutputTableSortState;
  toggleSort: (columnIndex: number) => void;
  calculateInView: boolean;
  experimentalShapashTableOptions?: ExperimentalShapashTableOptions;
}

function SortableRunQueryOutputTable({
  renderCellContent,
  runQueryOutput,
  rawOutputMode,
  sortState,
  toggleSort,
  calculateInView,
  experimentalShapashTableOptions,
}: SortableRunQueryOutputTableProps): JSX.Element {
  const sortedOutput = sortedRunQueryOutput(
    runQueryOutput,
    sortState,
    useResolvedChipItemIdMap(runQueryOutput).resolvedChipItemIdMap,
    rawOutputMode
  );

  function renderHeader(headerName: string, headerIndex: number): ReactNode {
    return (
      <SortableRunQueryOutputTableHeaderCell
        header={headerName}
        columnSortState={
          sortState?.index === headerIndex
            ? sortState.descending
              ? ColumnSortState.DESCENDING
              : ColumnSortState.ASCENDING
            : ColumnSortState.UNSORTED
        }
        onSortClick={() => {
          toggleSort(headerIndex);
        }}
      />
    );
  }

  return (
    <RunQueryOutputTable
      renderCellContent={renderCellContent}
      renderHeader={renderHeader}
      runQueryOutput={sortedOutput}
      calculateInView={calculateInView}
      experimentalShapashTableOptions={experimentalShapashTableOptions}
    />
  );
}

SortableRunQueryOutputTable.defaultProps = {
  renderCellContent: cellToString,
  calculateInView: false,
};

export default React.memo(SortableRunQueryOutputTable);
